/**
 * Backend capabilities the email link screens need but the API lacks (BE-210).
 * Off → the screen runs its mock in dev and hides the action in a production
 * build, so a deployed stand never claims "unsubscribed" when nothing changed.
 * On → the real call. Enable per capability as the backend ships:
 *   VITE_EMAIL_LINKS_API="confirm_email_change,secure_account"   (or "all")
 *
 * Email verification (`GET /auth/verify-email`) and turning an alert off
 * (`DELETE /notifications/subscriptions/unsubscribe`) already exist and are
 * always real.
 */
export const EMAIL_LINKS_API_CAPABILITIES = [
  /** `POST /auth/change-email/confirm { token }` → `{ email }` (email 04) */
  'confirm_email_change',
  /** `POST /auth/secure-account { token, password }` (emails 03 / 05) */
  'secure_account',
  /** `POST|DELETE /notifications/groups/unsubscribe?token=` → `{ group }` (marketing footers) */
  'group_unsubscribe',
  /** `POST /notifications/subscriptions/resubscribe?token=` — Undo after "Turn off this alert" */
  'alert_undo',
] as const

export type EmailLinksApiCapability = (typeof EMAIL_LINKS_API_CAPABILITIES)[number]

function parseEnv(raw: string | undefined): ReadonlySet<EmailLinksApiCapability> {
  const tokens = (raw ?? '')
    .split(',')
    .map((token) => token.trim())
    .filter(Boolean)
  if (tokens.includes('all')) {
    return new Set(EMAIL_LINKS_API_CAPABILITIES)
  }
  return new Set(
    tokens.filter((token): token is EmailLinksApiCapability =>
      (EMAIL_LINKS_API_CAPABILITIES as readonly string[]).includes(token),
    ),
  )
}

const enabled = parseEnv(import.meta.env.VITE_EMAIL_LINKS_API)

export function hasEmailLinksApi(capability: EmailLinksApiCapability): boolean {
  return enabled.has(capability)
}

/** The action is offered: real API, or its mock on a dev server. */
export function isEmailLinksActionAvailable(capability: EmailLinksApiCapability): boolean {
  return hasEmailLinksApi(capability) || import.meta.env.DEV
}
